import fs from 'fs';
import path from 'path';
import os from 'os';
import { logger } from '../utils/logger';
import { DSLRendererProps, DSLRendererPropsSchema } from './types';

/**
 * DashboardManager class to handle CRUD operations on dashboards
 * All operations read/write directly to files (no in-memory caching)
 */
export class DashboardManager {
  private dashboardsBasePath: string;
  private projectId: string;

  /**
   * Initialize DashboardManager with project ID
   * @param projectId - Project ID to use in file path
   */
  constructor(projectId: string = 'snowflake-dataset') {
    this.projectId = projectId;
    this.dashboardsBasePath = path.join(
      os.homedir(),
      '.superatom',
      'projects',
      projectId,
      'dashboards'
    );
  }

  /**
   * Get the file path for a specific dashboard
   * @param dashboardId - Dashboard ID
   * @returns Full path to dashboard data.json file
   */
  private getDashboardPath(dashboardId: string): string {
    return path.join(this.dashboardsBasePath, dashboardId, 'data.json');
  }

  /**
   * Create a new dashboard
   * @param dashboardId - Unique dashboard ID
   * @param dashboard - Dashboard data
   * @returns Created dashboard with metadata
   */
  createDashboard(dashboardId: string, dashboard: DSLRendererProps): DSLRendererProps {
    const dashboardPath = this.getDashboardPath(dashboardId);
    const dashboardDir = path.dirname(dashboardPath);

    // Check if dashboard already exists
    if (fs.existsSync(dashboardPath)) {
      throw new Error(`Dashboard '${dashboardId}' already exists`);
    }

    // Validate dashboard structure
    const validated = DSLRendererPropsSchema.parse(dashboard);

    // Create directory structure
    fs.mkdirSync(dashboardDir, { recursive: true });

    // Write dashboard to file
    fs.writeFileSync(dashboardPath, JSON.stringify(validated, null, 4));

    logger.info(`Dashboard created: ${dashboardId}`);
    return validated;
  }

  /**
   * Get a specific dashboard by ID
   * @param dashboardId - Dashboard ID
   * @returns Dashboard data or null if not found
   */
  getDashboard(dashboardId: string): DSLRendererProps | null {
    const dashboardPath = this.getDashboardPath(dashboardId);

    if (!fs.existsSync(dashboardPath)) {
      logger.warn(`Dashboard not found: ${dashboardId}`);
      return null;
    }

    try {
      const fileContent = fs.readFileSync(dashboardPath, 'utf-8');
      const dashboard = JSON.parse(fileContent) as DSLRendererProps;

      // Validate structure
      const validated = DSLRendererPropsSchema.parse(dashboard);
      return validated;
    } catch (error) {
      logger.error(`Failed to read dashboard ${dashboardId}:`, error);
      return null;
    }
  }

  /**
   * Get all dashboards
   * @returns Array of dashboard objects with their IDs
   */
  getAllDashboards(): Array<{ dashboardId: string; dashboard: DSLRendererProps }> {
    // Create base directory if it doesn't exist
    if (!fs.existsSync(this.dashboardsBasePath)) {
      fs.mkdirSync(this.dashboardsBasePath, { recursive: true });
      return [];
    }

    const dashboards: Array<{ dashboardId: string; dashboard: DSLRendererProps }> = [];

    try {
      const dashboardDirs = fs.readdirSync(this.dashboardsBasePath);

      for (const dashboardId of dashboardDirs) {
        const dashboardPath = this.getDashboardPath(dashboardId);

        if (fs.existsSync(dashboardPath)) {
          const dashboard = this.getDashboard(dashboardId);
          if (dashboard) {
            dashboards.push({ dashboardId, dashboard });
          }
        }
      }

      logger.debug(`Retrieved ${dashboards.length} dashboards`);
      return dashboards;
    } catch (error) {
      logger.error('Failed to get all dashboards:', error);
      return [];
    }
  }

  /**
   * Update an existing dashboard
   * @param dashboardId - Dashboard ID
   * @param dashboard - Updated dashboard data
   * @returns Updated dashboard or null if not found
   */
  updateDashboard(dashboardId: string, dashboard: DSLRendererProps): DSLRendererProps | null {
    const dashboardPath = this.getDashboardPath(dashboardId);

    if (!fs.existsSync(dashboardPath)) {
      logger.warn(`Dashboard not found for update: ${dashboardId}`);
      return null;
    }

    try {
      // Validate dashboard structure
      const validated = DSLRendererPropsSchema.parse(dashboard);

      // Write updated dashboard to file
      fs.writeFileSync(dashboardPath, JSON.stringify(validated, null, 4));

      logger.info(`Dashboard updated: ${dashboardId}`);
      return validated;
    } catch (error) {
      logger.error(`Failed to update dashboard ${dashboardId}:`, error);
      throw error;
    }
  }

  /**
   * Delete a dashboard
   * @param dashboardId - Dashboard ID
   * @returns True if deleted, false if not found
   */
  deleteDashboard(dashboardId: string): boolean {
    const dashboardPath = this.getDashboardPath(dashboardId);
    const dashboardDir = path.dirname(dashboardPath);

    if (!fs.existsSync(dashboardPath)) {
      logger.warn(`Dashboard not found for deletion: ${dashboardId}`);
      return false;
    }

    try {
      // Delete the entire dashboard directory
      fs.rmSync(dashboardDir, { recursive: true, force: true });

      logger.info(`Dashboard deleted: ${dashboardId}`);
      return true;
    } catch (error) {
      logger.error(`Failed to delete dashboard ${dashboardId}:`, error);
      return false;
    }
  }

  /**
   * Check if a dashboard exists
   * @param dashboardId - Dashboard ID
   * @returns True if dashboard exists, false otherwise
   */
  dashboardExists(dashboardId: string): boolean {
    const dashboardPath = this.getDashboardPath(dashboardId);
    return fs.existsSync(dashboardPath);
  }

  /**
   * Get dashboard count
   * @returns Number of dashboards
   */
  getDashboardCount(): number {
    if (!fs.existsSync(this.dashboardsBasePath)) {
      return 0;
    }

    try {
      const dashboardDirs = fs.readdirSync(this.dashboardsBasePath);
      return dashboardDirs.filter((dir) => {
        const dashboardPath = this.getDashboardPath(dir);
        return fs.existsSync(dashboardPath);
      }).length;
    } catch (error) {
      logger.error('Failed to get dashboard count:', error);
      return 0;
    }
  }
}
